import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../App';
import { customerApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Eye, EyeOff, Zap, Phone, User, Mail, Lock } from 'lucide-react';

export default function AccountSignupPage() {
  const { loginCustomer } = useCustomerAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', password: '', confirmPassword: '',
    whatsapp_number: '', marketing_opt_in: true,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Name is required';
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit mobile number';
    if (!form.password || form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone,
        email: form.email.trim() || undefined,
        password: form.password,
        whatsapp_number: form.whatsapp_number || form.phone,
        marketing_opt_in: form.marketing_opt_in,
      };
      const res = await customerApi.signup(payload);
      loginCustomer(res.data.access_token, res.data.customer);
      toast.success(`Welcome to Scratch Unleashed, ${res.data.customer.full_name}!`);
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const F = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="hero-gradient pb-4">
        <div className="max-w-[420px] mx-auto px-5 pt-10">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={15} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-xl font-semibold">Scratch Unleashed</span>
          </div>
          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Create account</h1>
          <p className="text-[#B9B9B9] text-sm">Join to access bookings and exclusive events</p>
        </div>
      </div>

      <div className="max-w-[420px] mx-auto px-5 py-5 pb-24">
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-signup-form">
          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><User size={11} /> Full Name</Label>
            <Input data-testid="signup-name-input" placeholder="Arjun Sharma" value={form.full_name} onChange={e => F('full_name', e.target.value)}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-11" />
            {errors.full_name && <p className="text-[#E05252] text-xs">{errors.full_name}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Phone size={11} /> Mobile Number</Label>
            <Input data-testid="signup-phone-input" type="tel" inputMode="numeric" placeholder="9876543210" value={form.phone} onChange={e => F('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11" />
            {errors.phone && <p className="text-[#E05252] text-xs">{errors.phone}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Mail size={11} /> Email <span className="text-[#6F6F6F]">(optional)</span></Label>
            <Input data-testid="signup-email-input" type="email" placeholder="arjun@example.com" value={form.email} onChange={e => F('email', e.target.value)}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11 placeholder:text-[#4A4A4A]" />
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Lock size={11} /> Password</Label>
            <div className="relative">
              <Input type={showPwd ? 'text' : 'password'} placeholder="Min. 6 characters" value={form.password} onChange={e => F('password', e.target.value)}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11 pr-10" />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6F6F]">{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
            {errors.password && <p className="text-[#E05252] text-xs">{errors.password}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs">Confirm Password</Label>
            <Input type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => F('confirmPassword', e.target.value)}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11" />
            {errors.confirmPassword && <p className="text-[#E05252] text-xs">{errors.confirmPassword}</p>}
          </div>

          {/* Marketing opt-in */}
          <div className="flex items-center justify-between py-1">
            <Label className="text-[#B9B9B9] text-xs">Receive event updates & offers</Label>
            <Switch checked={form.marketing_opt_in} onCheckedChange={v => F('marketing_opt_in', v)} />
          </div>

          <Button type="submit" data-testid="signup-submit-button" disabled={loading}
            className="w-full btn-gold h-12 rounded-xl font-semibold text-base !mt-5">
            {loading ? <div className="w-4 h-4 border-2 border-[#120F08] border-t-transparent rounded-full animate-spin" /> : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-[#B9B9B9] text-sm mt-5">
          Already have an account?{' '}
          <Link to="/account/login" className="text-[#D4A843] hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
